import { Icon, IconicButton, Text } from "@nulogy/components";
import axios from "axios";
import Section from "components/common/Section";
import { format24HourDateTime } from "components/helpers/format";
import endpoints from "config/endpoints";
import useGetItemMetadata from "hooks/apollo/item/useGetItemMetadata";
import useGetWorkOrderMetadata from "hooks/apollo/workOrder/useGetWorkOrderMetadata";
import React, { useState } from "react";
import styled from "styled-components";

const FILENAMES = {
  items: "Items",
  workOrders: "Work orders",
};

const MetaData = styled(Text)(({ theme }) => ({
  color: theme.colors.darkGrey,
  fontSize: theme.fontSizes.smaller,
  lineHeight: theme.sizes.x2,
  marginTop: theme.space.x1,
}));

function downloadFile(body, filename) {
  const newUrl = window.URL.createObjectURL(new Blob([body]));
  const link = document.createElement("a");
  link.href = newUrl;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

export default function Exports() {
  const [downloading, setDownloading] = useState({ items: false, workOrders: false });
  const { data: itemMetadata, loading: itemMetadataLoading } = useGetItemMetadata();
  const { data: workOrderMetadata, loading: workOrderMetadataLoading } = useGetWorkOrderMetadata();

  const handleExport = (url, type) => {
    setDownloading({ ...downloading, [type]: true });

    axios.get(url, { withCredentials: true }).then(({ data }) => {
      setDownloading({ ...downloading, [type]: false });
      const timestamp = format24HourDateTime(new Date());
      const filename = `${FILENAMES[type]} ${timestamp}.csv`;
      downloadFile(data, filename);
    });
  };

  const itemMetadataAvailable = itemMetadata && !itemMetadataLoading;
  const itemCount = itemMetadata?.itemMetadata.count;
  const itemExportDisabled = !itemMetadataAvailable || !itemCount;

  const itemMetadataText = () => {
    if (!itemMetadataAvailable) {
      return "Fetching...";
    }
    if (!itemCount) {
      return "There are no items to export yet. To start, go to the imports tab.";
    }
    return `Current item count: ${itemCount}`;
  };

  const workOrderMetadataAvailable = workOrderMetadata && !workOrderMetadataLoading;
  const workOrderCount = workOrderMetadata?.workOrderMetadata.count;
  const workOrderExportDisabled = !workOrderMetadataAvailable || !workOrderCount;

  const workOrderMetadataText = () => {
    if (!workOrderMetadataAvailable) {
      return "Fetching...";
    }
    if (!workOrderCount) {
      return "There are no work orders to export yet. To start, go to the imports tab.";
    }
    return `Current work order count: ${workOrderCount}`;
  };

  return (
    <>
      <Section marginTop="x3" title="Items">
        <Text>Export a .csv file of all items in Production Scheduling.</Text>
        <MetaData>{itemMetadataText()}</MetaData>
        <IconicButton
          disabled={itemExportDisabled || downloading.items}
          icon="getApp"
          onClick={() => handleExport(endpoints.exportItems, "items")}
        >
          Export items
        </IconicButton>
        {downloading.items && <Icon icon="loading" marginLeft="x1" />}
      </Section>
      <Section marginTop="x3" title="Work orders">
        <Text>Export a .csv file of all work orders in Production Scheduling.</Text>
        <MetaData>{workOrderMetadataText()}</MetaData>
        <IconicButton
          disabled={workOrderExportDisabled || downloading.workOrders}
          icon="getApp"
          onClick={() => handleExport(endpoints.exportWorkOrders, "workOrders")}
        >
          Export work orders
        </IconicButton>
        {downloading.workOrders && <Icon icon="loading" marginLeft="x1" />}
      </Section>
    </>
  );
}
